import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../api/apiClient";
import PageHeader from "../../../app/layout/PageHeader";
import "../../../css/tables.css";

interface Row {
  partyId: number; displayName: string; partyType: string; createdDate: string;
  isTenantOrganisation: boolean; householdIds: number[]; hasLogin: boolean; hasHistoricalEnrollment: boolean;
}

type SortColumn = "displayName" | "partyType" | "details";
const enrollmentDetails = (row: Row) => row.isTenantOrganisation ? "Tenant setup exception" : row.hasHistoricalEnrollment ? "Historical enrollments only" : "No enrollment";
const rowDetails = (row: Row) => [enrollmentDetails(row), row.hasLogin ? "Linked login" : "", row.householdIds.length ? `Family ${row.householdIds.join(", ")}` : ""].filter(Boolean).join(" · ");

export default function PartyIntegrityPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [enrollmentFilter, setEnrollmentFilter] = useState("");
  const [loginFilter, setLoginFilter] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("displayName");
  const [sortAscending, setSortAscending] = useState(true);
  useEffect(() => {
    api<Row[]>("/api/system/party-integrity").then(setRows).catch(() => setFailed(true)).finally(() => setLoading(false));
  }, []);
  const filtered = rows.filter(row =>
    `${row.displayName} ${row.partyType} ${rowDetails(row)}`.toLowerCase().includes(search.trim().toLowerCase()) &&
    (!typeFilter || row.partyType === typeFilter) &&
    (!enrollmentFilter || enrollmentDetails(row) === enrollmentFilter) &&
    (!loginFilter || row.hasLogin === (loginFilter === "linked"))
  ).sort((a, b) => {
    const value = (row: Row) => sortColumn === "details" ? rowDetails(row) : row[sortColumn];
    return value(a).localeCompare(value(b), "en-GB", { numeric: true, sensitivity: "base" }) * (sortAscending ? 1 : -1);
  });
  const sortHeading = (column: SortColumn, label: string) => <th scope="col" aria-sort={sortColumn === column ? (sortAscending ? "ascending" : "descending") : "none"}>
    <button type="button" className="app-table-sort" onClick={() => {
      setSortAscending(sortColumn === column ? !sortAscending : true); setSortColumn(column);
    }}>{label}<span aria-hidden="true">{sortColumn === column ? (sortAscending ? "↑" : "↓") : "↕"}</span></button>
  </th>;
  return <div className="page-container">
    <PageHeader title="Party service audit" subtitle="Active parties across all organisations without an active service enrollment or owned service." />
    <div className="card">
      <div className="app-table-filters">
        <div><label className="form-label" htmlFor="party-integrity-search">Search parties</label>
          <input id="party-integrity-search" className="form-input" placeholder="Search names or details" value={search} onChange={event => setSearch(event.target.value)} /></div>
        <div><label className="form-label" htmlFor="party-integrity-type">Party type</label>
          <select id="party-integrity-type" className="form-input" value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
            <option value="">All types</option>{Array.from(new Set(rows.map(row => row.partyType))).sort().map(type => <option key={type}>{type}</option>)}
          </select></div>
        <div><label className="form-label" htmlFor="party-integrity-enrollment">Enrollment</label>
          <select id="party-integrity-enrollment" className="form-input" value={enrollmentFilter} onChange={event => setEnrollmentFilter(event.target.value)}>
            <option value="">All enrollment issues</option><option>No enrollment</option><option>Historical enrollments only</option><option>Tenant setup exception</option>
          </select></div>
        <div><label className="form-label" htmlFor="party-integrity-login">Login</label>
          <select id="party-integrity-login" className="form-input" value={loginFilter} onChange={event => setLoginFilter(event.target.value)}>
            <option value="">All people</option><option value="linked">Linked login</option><option value="unlinked">No linked login</option>
          </select></div>
      </div>
      {(search || typeFilter || enrollmentFilter || loginFilter) && <button type="button" className="link-button" onClick={() => { setSearch(""); setTypeFilter(""); setEnrollmentFilter(""); setLoginFilter(""); }}>Clear filters</button>}
      <p className="field-hint" aria-live="polite">{loading ? "Loading..." : failed ? "Audit unavailable." : `${filtered.length} of ${rows.length} parties shown.`} Tenant organisations awaiting service setup are shown as setup exceptions.</p>
      <div className="app-table-container"><table className="app-data-table" aria-label="Party service audit">
        <thead><tr>{sortHeading("displayName", "Party")}{sortHeading("partyType", "Type")}{sortHeading("details", "Details")}<th scope="col">Action</th></tr></thead>
        <tbody>{filtered.map(row => <tr key={row.partyId}>
          <td>{row.displayName}</td><td>{row.partyType}</td>
          <td>{row.isTenantOrganisation ? "Tenant setup exception" : row.hasHistoricalEnrollment ? "Historical enrollments only" : "No enrollment"}{row.hasLogin && " · Linked login"}{row.householdIds.length > 0 && ` · Family ${row.householdIds.join(", ")}`}</td>
          <td><Link to={{ pathname: "/manage-party", state: { partyId: row.partyId, partyType: row.partyType, returnTo: "/admin/system/party-integrity" } }}>Review</Link></td>
        </tr>)}</tbody>
      </table></div>
      {failed && <p>Unable to load the party service audit. Refresh the page to try again.</p>}
      {!loading && !failed && filtered.length === 0 && <p>{rows.length ? "No parties match your filters." : "No unlinked parties found."}</p>}
    </div>
  </div>;
}
